import type { ParseContentResult } from './parse';
import { DEFAULT_CARD_ORDER, type Card, type UseCasesFile } from './types';

/**
 * カードの置き場 `content/<unit>/<id>.yaml` から unit とファイル名を取る（card-schema.md §1）。
 * unit はファイルの親ディレクトリ名。`content/` 直下のファイル（use-cases.yaml）は null。
 */
export function cardPathInfo(path: string): { unit: string | null; fileName: string } {
  const parts = path.split('/').filter(Boolean);
  const fileName = parts[parts.length - 1] ?? path;
  const parent = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  const unit = parent !== undefined && parent !== 'content' ? parent : null;
  return { unit, fileName };
}

export interface IndexedCard {
  card: Card;
  path: string;
  /** ファイル内の添字。 */
  position: number;
}

export interface UnitEntry {
  unit: string;
  stage: number;
  useCase: string | null;
  cardIds: string[];
}

export interface CardCatalog {
  /** カード順（stage → unit 名 → ファイル名 → ファイル内の順。progress-store.md §4.4）。 */
  cards: Card[];
  entries: IndexedCard[];
  byId: ReadonlyMap<string, Card>;
  /** formerIds → 現在の id。 */
  formerIdMap: ReadonlyMap<string, string>;
  units: UnitEntry[];
  byStage: ReadonlyMap<number, Card[]>;
  useCases: UseCasesFile | null;
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * カード順の比較（card-schema.md §1、progress-store.md §4.4）。
 * stage → unit 名 → order（省略は 1000）→ ファイル名 → パス → ファイル内の順。
 */
export function compareCardOrder(a: IndexedCard, b: IndexedCard): number {
  return (
    a.card.stage - b.card.stage ||
    cmp(a.card.unit, b.card.unit) ||
    (a.card.order ?? DEFAULT_CARD_ORDER) - (b.card.order ?? DEFAULT_CARD_ORDER) ||
    cmp(cardPathInfo(a.path).fileName, cardPathInfo(b.path).fileName) ||
    cmp(a.path, b.path) ||
    a.position - b.position
  );
}

/**
 * 読み込んだファイル群からインデックスを作る。parse に失敗したファイルは無視する（検証は verify-cards が行う）。
 * id が重複したら先に来た方を残す。
 */
export function buildCatalog(files: readonly ParseContentResult[]): CardCatalog {
  const entries: IndexedCard[] = [];
  let useCases: UseCasesFile | null = null;
  for (const f of files) {
    if (!f.ok) continue;
    if ('kind' in f.file) {
      useCases = f.file;
      continue;
    }
    f.file.cards.forEach((card, position) => entries.push({ card, path: f.path, position }));
  }
  entries.sort(compareCardOrder);
  const byId = new Map<string, Card>();
  const formerIdMap = new Map<string, string>();
  const unique: IndexedCard[] = [];
  for (const e of entries) {
    if (byId.has(e.card.id)) continue;
    byId.set(e.card.id, e.card);
    unique.push(e);
    for (const old of e.card.formerIds ?? []) formerIdMap.set(old, e.card.id);
  }
  const cards = unique.map((e) => e.card);
  const unitMap = new Map<string, UnitEntry>();
  const byStage = new Map<number, Card[]>();
  for (const c of cards) {
    let u = unitMap.get(c.unit);
    if (!u) {
      u = { unit: c.unit, stage: c.stage, useCase: useCases?.units[c.unit] ?? null, cardIds: [] };
      unitMap.set(c.unit, u);
    }
    u.stage = Math.min(u.stage, c.stage);
    u.cardIds.push(c.id);
    const list = byStage.get(c.stage) ?? [];
    list.push(c);
    byStage.set(c.stage, list);
  }
  return {
    cards,
    entries: unique,
    byId,
    formerIdMap,
    units: [...unitMap.values()].sort((a, b) => a.stage - b.stage || cmp(a.unit, b.unit)),
    byStage,
    useCases,
  };
}

/** 出題してよいカード（draft でない）。 */
export function publishedCards(catalog: CardCatalog): Card[] {
  return catalog.cards.filter((c) => !c.draft);
}

/** textbook から # 以降を除いた章のパス（card-schema.md §2.3）。 */
export function textbookChapter(textbook: string): string {
  const i = textbook.indexOf('#');
  return i >= 0 ? textbook.slice(0, i) : textbook;
}

/**
 * カードの useCase を解決する（card-schema.md §2.3）。
 * カードの useCase → textbooks[章] → units[unit] の順で最初に見つかった値。無ければ null。
 * textbook の無い memo（§3.9）は章を飛ばしてユニットの値を使う。
 */
export function resolveUseCase(
  card: Pick<Card, 'useCase' | 'textbook' | 'unit'>,
  useCases: Partial<Pick<UseCasesFile, 'textbooks' | 'units'>> | null,
): string | null {
  if (card.useCase !== undefined && card.useCase !== '') return card.useCase;
  const chapter =
    card.textbook !== undefined ? useCases?.textbooks?.[textbookChapter(card.textbook)] : undefined;
  return chapter ?? useCases?.units?.[card.unit] ?? null;
}
